import { useState, useRef, useEffect, useMemo } from 'react';
import SchedulingRoleCols from '../SchedulingRoleCols';
import './styles.scss';
import useOutsideClick from '../../../../../hook/useOutsideClick';
import Portal from '../../../../../components/other/portal';
import AddUnitRoleModal from '../AddUnitRoleModal';
import cx from 'classnames';
import { isMobile } from 'react-device-detect';
import { usePermission } from '../../../../../container/permissionContext';
import { Tooltip } from 'react-tooltip';
import { useAuth } from '../../../../../container/authContext';
import { getRoleLabelsService } from '../../../../../services/company';

const SchedulingUnitRow = ({
  unit,
  handleAddUnitRole,
  editSidebarSelectedUser,
  editSidebarAction,
  editSidebarFormData,
  editSidebarSelectedItems,
  setEditSidebarSelectedItems,
  periodData,
  getPeriodData,
  handleUpdateUnitRole,
  shiftsData,
  setPeriodData,
  isPastPeriod,
  unitIndex,
  cellAssignmentData,
  setCellAssignmentData,
  showRecurringShifts,
  roleLabels,
  isSelectingPostShiftsActive,
  setSelectedShiftsToPost,
  selectedShiftsToPost,
  isFirstNurseRole = false,
  isPrint = false,
  showCensus = false,
  unitCensus,
}) => {
  const { user } = useAuth();

  const buttonRef = useRef();
  const dropdownRef = useRef();
  const { hasPermissionByCodeName } = usePermission();
  const hasWritePermission = hasPermissionByCodeName('scheduling_management');

  const [dropdownPos, setDropdownPos] = useState(null);
  const [isAddRoleDropdownOpen, setIsAddRoleDropdownOpen] = useState(false);
  const [addRoleData, setAddRoleData] = useState(null);

  useOutsideClick(dropdownRef, () => setIsAddRoleDropdownOpen(false));

  useEffect(() => {
    if (isAddRoleDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isAddRoleDropdownOpen]);

  const orderedUnitRoles = useMemo(() => {
    return unit.roles.slice().sort((a, b) => a.role.localeCompare(b.role));
  }, [unit.roles]);

  const nurses = useMemo(() => {
    return orderedUnitRoles.filter((role) => role.role === 'nurse');
  }, [orderedUnitRoles]);

  const cnas = useMemo(() => {
    return orderedUnitRoles.filter((role) => role.role === 'cna');
  }, [orderedUnitRoles]);

  return (
    <>
      <tr
        className={cx('unit-row', {
          isMobile,
          'first-row-in-table': isFirstNurseRole,
          'first-nurse-role': unitIndex !== 0 && !isFirstNurseRole,
        })}
      >
        <td rowSpan={unit.roles.length} className="unit-name-col">
          <div className="unit-name-col-wrapper">
            {isMobile || !hasWritePermission ? (
              <>{unit.unit.name}</>
            ) : (
              <button
                ref={buttonRef}
                onClick={(e) => setIsAddRoleDropdownOpen(true)}
                className="btn btn-outline unit-dropdown-btn"
                disabled={isPrint}
              >
                {isPrint ? (
                  <>{unit.unit.name}</>
                ) : (
                  <>
                    {/* <Tooltip position="fixed" opacity={1} id={`${unit.id}-unit-tooltip`} content={unit.unit.name} /> */}
                    <span data-tooltip-id={`${unit.id}-unit-tooltip`}>{unit.unit.name}</span>
                  </>
                )}
                {!isPrint && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M9.99984 10.832C10.4601 10.832 10.8332 10.4589 10.8332 9.9987C10.8332 9.53846 10.4601 9.16536 9.99984 9.16536C9.5396 9.16536 9.1665 9.53846 9.1665 9.9987C9.1665 10.4589 9.5396 10.832 9.99984 10.832Z"
                      fill="#667085"
                    />
                    <path
                      d="M9.99984 4.9987C10.4601 4.9987 10.8332 4.6256 10.8332 4.16536C10.8332 3.70513 10.4601 3.33203 9.99984 3.33203C9.5396 3.33203 9.1665 3.70513 9.1665 4.16536C9.1665 4.6256 9.5396 4.9987 9.99984 4.9987Z"
                      fill="#667085"
                    />
                    <path
                      d="M9.99984 16.6654C10.4601 16.6654 10.8332 16.2923 10.8332 15.832C10.8332 15.3718 10.4601 14.9987 9.99984 14.9987C9.5396 14.9987 9.1665 15.3718 9.1665 15.832C9.1665 16.2923 9.5396 16.6654 9.99984 16.6654Z"
                      fill="#667085"
                    />
                    <path
                      d="M9.99984 10.832C10.4601 10.832 10.8332 10.4589 10.8332 9.9987C10.8332 9.53846 10.4601 9.16536 9.99984 9.16536C9.5396 9.16536 9.1665 9.53846 9.1665 9.9987C9.1665 10.4589 9.5396 10.832 9.99984 10.832Z"
                      stroke="#667085"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.99984 4.9987C10.4601 4.9987 10.8332 4.6256 10.8332 4.16536C10.8332 3.70513 10.4601 3.33203 9.99984 3.33203C9.5396 3.33203 9.1665 3.70513 9.1665 4.16536C9.1665 4.6256 9.5396 4.9987 9.99984 4.9987Z"
                      stroke="#667085"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.99984 16.6654C10.4601 16.6654 10.8332 16.2923 10.8332 15.832C10.8332 15.3718 10.4601 14.9987 9.99984 14.9987C9.5396 14.9987 9.1665 15.3718 9.1665 15.832C9.1665 16.2923 9.5396 16.6654 9.99984 16.6654Z"
                      stroke="#667085"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            )}
            {isPrint && showCensus && (
              <div className="unit-census-print">
                Census: <span style={{ fontSize: 14 }}>{unitCensus}</span>
              </div>
            )}
          </div>
        </td>
        <SchedulingRoleCols
          periodData={periodData}
          editSidebarSelectedItems={editSidebarSelectedItems}
          editSidebarAction={editSidebarAction}
          editSidebarFormData={editSidebarFormData}
          setEditSidebarSelectedItems={setEditSidebarSelectedItems}
          editSidebarSelectedUser={editSidebarSelectedUser}
          isSelectingPostShiftsActive={isSelectingPostShiftsActive}
          setSelectedShiftsToPost={setSelectedShiftsToPost}
          role={nurses[0]}
          getPeriodData={getPeriodData}
          roleLabels={roleLabels}
          canDelete={nurses.length > 1 && !nurses[0].permanent_role?.is_default}
          order={1}
          shiftsData={shiftsData}
          isPastPeriod={isPastPeriod}
          isPrint={isPrint}
          unit={unit}
          handleUpdateUnitRole={handleUpdateUnitRole}
          setPeriodData={setPeriodData}
          cellAssignmentData={cellAssignmentData}
          setCellAssignmentData={setCellAssignmentData}
          showRecurringShifts={showRecurringShifts}
          selectedShiftsToPost={selectedShiftsToPost}
        />
      </tr>
      {nurses.slice(1).map((role, index) => (
        <tr className={cx('unit-row', { isMobile })} key={`${role.name}-${role.id}-${index}`}>
          <SchedulingRoleCols
            periodData={periodData}
            editSidebarSelectedItems={editSidebarSelectedItems}
            editSidebarAction={editSidebarAction}
            editSidebarFormData={editSidebarFormData}
            setEditSidebarSelectedItems={setEditSidebarSelectedItems}
            editSidebarSelectedUser={editSidebarSelectedUser}
            getPeriodData={getPeriodData}
            role={role}
            roleLabels={roleLabels}
            canDelete={nurses.length > 1 && !role.permanent_role?.is_default}
            isPastPeriod={isPastPeriod}
            isPrint={isPrint}
            order={index + 2}
            unit={unit}
            shiftsData={shiftsData}
            handleUpdateUnitRole={handleUpdateUnitRole}
            setPeriodData={setPeriodData}
            showRecurringShifts={showRecurringShifts}
            cellAssignmentData={cellAssignmentData}
            setCellAssignmentData={setCellAssignmentData}
            isSelectingPostShiftsActive={isSelectingPostShiftsActive}
            setSelectedShiftsToPost={setSelectedShiftsToPost}
            selectedShiftsToPost={selectedShiftsToPost}
          />
        </tr>
      ))}
      {cnas.map((role, index) => (
        <tr
          key={`${role.name}-${role.id}-${index}`}
          className={cx(`unit-row ${role.name}-${role.id}-${index}`, { isMobile, 'first-cna-role': index === 0 })}
        >
          <SchedulingRoleCols
            periodData={periodData}
            editSidebarSelectedItems={editSidebarSelectedItems}
            editSidebarAction={editSidebarAction}
            editSidebarFormData={editSidebarFormData}
            setEditSidebarSelectedItems={setEditSidebarSelectedItems}
            editSidebarSelectedUser={editSidebarSelectedUser}
            getPeriodData={getPeriodData}
            canDelete={cnas.length > 1 && !role.permanent_role?.is_default}
            isPastPeriod={isPastPeriod}
            unit={unit}
            roleLabels={roleLabels}
            isPrint={isPrint}
            role={role}
            order={index + 1}
            shiftsData={shiftsData}
            handleUpdateUnitRole={handleUpdateUnitRole}
            setPeriodData={setPeriodData}
            showRecurringShifts={showRecurringShifts}
            cellAssignmentData={cellAssignmentData}
            setCellAssignmentData={setCellAssignmentData}
            isSelectingPostShiftsActive={isSelectingPostShiftsActive}
            setSelectedShiftsToPost={setSelectedShiftsToPost}
            selectedShiftsToPost={selectedShiftsToPost}
          />
        </tr>
      ))}

      {isAddRoleDropdownOpen && dropdownPos && !isPrint && (
        <Portal>
          <ul
            ref={dropdownRef}
            className="unit-dropdown-menu"
            style={{
              position: 'absolute',
              top: `${dropdownPos.top}px`,
              left: `${dropdownPos.left}px`,
              zIndex: 9999,
            }}
          >
            <li>
              <button
                className="btn btn-left btn-full btn-text"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAddRoleDropdownOpen(false);
                  setAddRoleData({ type: 'Nurse', unitId: unit.id });
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 3V17M3 10H17" stroke="#101828" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Add {roleLabels?.nurse_role_label}
              </button>
            </li>
            <li>
              <button
                className="btn btn-left btn-full btn-text"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAddRoleDropdownOpen(false);
                  setAddRoleData({ type: 'CNA', unitId: unit.id });
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 3V17M3 10H17" stroke="#101828" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Add {roleLabels?.cna_role_label}
              </button>
            </li>
          </ul>
        </Portal>
      )}
      {!!addRoleData && !isPrint && (
        <AddUnitRoleModal
          handleAddUnitRole={handleAddUnitRole}
          closeModal={() => setAddRoleData(null)}
          addRoleData={addRoleData}
          roleLabels={roleLabels}
        />
      )}
    </>
  );
};

export default SchedulingUnitRow;
